/*
 * Created on Thu Oct 12 2023 10:24:49 AM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { useCallback, useEffect, useState } from 'react';
import FlexOfficeFooter from '../components/FlexOfficeFooter';
import FlexOfficeHeader from '../components/FlexOfficeHeader';

const ExploreLocation = ({ company }) => {
    const [companyAddress, setCompanyAddress] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const operatingLocationCode = company?.operatingLocation?.code || "US";

    const getCountryCode = useCallback((contactCode) => {
        if (contactCode) {
            return contactCode;
        }

        return operatingLocationCode === "IND" ? "+91" : "+1";
    }, [operatingLocationCode]);

    const formatPhoneNumber = useCallback((phone, contactCode) => {
        const digits = String(phone || "").replace(/\D/g, "");
        const resolvedCountryCode = getCountryCode(contactCode);

        if (digits.length === 10 && resolvedCountryCode === "+1") {
            return `${resolvedCountryCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }

        return [resolvedCountryCode, phone].filter(Boolean).join(" ").trim();
    }, [getCountryCode]);

    useEffect(() => {
        const companyData = JSON.parse(localStorage.getItem('company'));

        if (companyData) {
            // Extract address
            if (companyData.address) {
                const { streetName1, streetName2, city, state, zipcode, country } = companyData.address;
                const fullAddress = `${streetName1} ${streetName2}, ${city}, ${state}, ${zipcode}, ${country}`;
                setCompanyAddress(fullAddress);
            }

            // Extract contact phone and email
            if (companyData.contactPerson) {
                setContactPhone(formatPhoneNumber(companyData.contactPerson.phoneNumber, companyData.contactPerson.countryCode));
                setContactEmail(companyData.contactPerson.email);
            }
        }
    }, [formatPhoneNumber]);

    const logo = localStorage.getItem('companyLogo');
    const number = company?.contactPerson?.phoneNumber || "244-5333-7783";
    const code = getCountryCode(company?.contactPerson?.countryCode);
    const phoneNumber = formatPhoneNumber(number, code);
    const email = company?.contactPerson?.email || "avpstech.com";

    return (
        <>
            <div className="min-h-screen bg-gray-900">
                <FlexOfficeHeader />

                <main className="pt-24 pb-16 px-4 fx-m-page">
                    <div className="max-w-7xl mx-auto fx-m-full-bleed">
                        {/* Hero Section */}
                        <div className="text-center mb-16">
                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                Explore Our Locations
                            </h1>
                            <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}></div>
                            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                                Discover our premium office spaces designed for modern businesses
                            </p>
                        </div>

                        {/* Main Location Card */}
                        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-5xl mx-auto mb-16">
                            {/* Company Logo Section */}
                            {logo && (
                                <div className="bg-gray-50 p-8 text-center border-b">
                                    <img
                                        src={logo}
                                        alt="Company Logo"
                                        className="max-h-16 mx-auto object-contain"
                                    />
                                </div>
                            )}

                            <div className="p-8 md:p-12 fx-m-card">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                    {/* Address Section */}
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                                            <div className="w-8 h-8 mr-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}>
                                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            Our Location
                                        </h2>
                                        <div className="bg-gray-50 rounded-xl p-6 border-l-4" style={{ borderLeftColor: 'var(--flex-primary, #2563eb)' }}>
                                            <div className="flex items-start">
                                                <svg className="w-5 h-5 mt-1 mr-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                </svg>
                                                <div>
                                                    <p className="text-lg text-gray-800 leading-relaxed font-medium">
                                                        {companyAddress || "123 Office St, City, State, ZIP, Country"}
                                                    </p>
                                                    <p className="text-sm text-gray-600 mt-2">
                                                        Primary Business Location
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Map Placeholder */}
                                        <div className="mt-6 bg-gray-100 rounded-xl h-48 flex items-center justify-center border-2 border-dashed border-gray-300">
                                            <div className="text-center">
                                                <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                </svg>
                                                <p className="text-gray-500 font-medium">Interactive Map</p>
                                                <p className="text-sm text-gray-400">Coming Soon</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact Information */}
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                                            <div className="w-8 h-8 mr-3 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}>
                                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                </svg>
                                            </div>
                                            Contact Information
                                        </h2>

                                        <div className="space-y-4">
                                            {/* Phone */}
                                            <div className="bg-gray-50 rounded-xl p-6 border-l-4" style={{ borderLeftColor: 'var(--flex-primary, #2563eb)' }}>
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-500 font-medium">Phone Number</p>
                                                        <p className="text-lg font-semibold text-gray-800">{phoneNumber}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Email */}
                                            <div className="bg-gray-50 rounded-xl p-6 border-l-4" style={{ borderLeftColor: 'var(--flex-primary, #2563eb)' }}>
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4">
                                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-500 font-medium">Email Address</p>
                                                        <p className="text-lg font-semibold text-gray-800">{email}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Additional Contact from localStorage */}
                                            {contactPhone && (
                                                <div className="bg-gray-50 rounded-xl p-6 border-l-4" style={{ borderLeftColor: 'var(--flex-primary, #2563eb)' }}>
                                                    <div className="flex items-center">
                                                        <div
                                                            className="w-10 h-10 rounded-full flex items-center justify-center mr-4"
                                                            style={{ backgroundColor: 'var(--flex-accent-soft, rgba(20, 184, 166, 0.14))' }}
                                                        >
                                                            <svg className="w-5 h-5" style={{ color: 'var(--flex-accent, #14b8a6)' }} fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500 font-medium">Direct Contact</p>
                                                            <p className="text-lg font-semibold text-gray-800">{contactPhone}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {contactEmail && (
                                                <div className="bg-gray-50 rounded-xl p-6 border-l-4" style={{ borderLeftColor: 'var(--flex-primary, #2563eb)' }}>
                                                    <div className="flex items-center">
                                                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-4">
                                                            <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm text-gray-500 font-medium">Direct Email</p>
                                                            <p className="text-lg font-semibold text-gray-800">{contactEmail}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                                    <button
                                        className="text-white px-8 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition-all duration-200 shadow-lg transform hover:scale-105"
                                        style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}
                                    >
                                        Schedule a Visit
                                    </button>
                                    <button className="bg-gray-100 text-gray-800 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-200 transition-all duration-200 shadow-lg transform hover:scale-105">
                                        Get Directions
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Features Section */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 fx-m-grid-1">
                            <div className="bg-white rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-shadow duration-300 fx-m-card">
                                <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}>
                                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Secure Access</h3>
                                <p className="text-gray-600">24/7 secure access with advanced keycard entry systems</p>
                            </div>

                            <div className="bg-white rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-shadow duration-300 fx-m-card">
                                <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}>
                                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">Flexible Spaces</h3>
                                <p className="text-gray-600">Adaptable office layouts designed for teams of any size</p>
                            </div>

                            <div className="bg-white rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-shadow duration-300 fx-m-card">
                                <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--flex-primary, #2563eb)' }}>
                                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">High-Speed Internet</h3>
                                <p className="text-gray-600">Fiber optic internet for seamless connectivity and productivity</p>
                            </div>
                        </div>
                    </div>
                </main>

                <FlexOfficeFooter />
            </div>
        </>
    );
};

export default ExploreLocation;
